/** Utilidades de fecha centralizadas (zona Bogotá, rangos, etc.) */

export function getBogotaDate(): Date {
  const now = new Date()
  return new Date(now.toLocaleString('en-US', { timeZone: 'America/Bogota' }))
}

export function getCurrentYearRange(): { desde: string; hasta: string } {
  const year = new Date().getFullYear()
  return {
    desde: `${year}-01-01`,
    hasta: `${year}-12-31`,
  }
}

export function getCurrentDayRangeBogota(): { desde: string; hasta: string } {
  const bogotaDate = getBogotaDate()
  const year = bogotaDate.getFullYear()
  const month = String(bogotaDate.getMonth() + 1).padStart(2, '0')
  const day = String(bogotaDate.getDate()).padStart(2, '0')
  const dateStr = `${year}-${month}-${day}`
  return { desde: dateStr, hasta: dateStr }
}

export function formatBogotaDateLong(locale = 'es-ES'): string {
  return getBogotaDate().toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function getMonthName(month: number): string {
  const months = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ]
  return months[month - 1] || ''
}

export type AppLanguage = 'es' | 'en' | 'ca'

export function getLocale(language: AppLanguage = 'es'): string {
  switch (language) {
    case 'en':
      return 'en-US'
    case 'ca':
      return 'ca-ES'
    default:
      return 'es-ES'
  }
}

/** Extrae HH:MM del string ISO sin conversión de zona horaria */
export function formatTime(dateString: string, language: AppLanguage = 'es'): string {
  if (dateString.includes('T')) {
    const timePart = dateString.split('T')[1]
    if (timePart.includes('Z')) {
      const time = timePart.replace('Z', '').split('.')[0]
      return time.slice(0, 5)
    }
    if (timePart.includes('+') || timePart.includes('-')) {
      const time = timePart.split(/[+-]/)[0]
      return time.slice(0, 5)
    }
    return timePart.slice(0, 5)
  }

  const date = new Date(dateString)
  return date.toLocaleTimeString(getLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function getMinutesFromDateString(dateString: string): number {
  if (dateString.includes('T')) {
    const timePart = dateString.split('T')[1]
    let time = ''

    if (timePart.includes('Z')) {
      time = timePart.replace('Z', '').split('.')[0]
    } else if (timePart.includes('+') || timePart.includes('-')) {
      time = timePart.split(/[+-]/)[0]
    } else {
      time = timePart
    }

    const minutes = time.split(':')[1]
    return parseInt(minutes, 10) || 0
  }

  return new Date(dateString).getMinutes()
}

export function getHourFromDateString(dateString: string): number {
  if (dateString.includes('T')) {
    const timePart = dateString.split('T')[1]
    let time = ''

    if (timePart.includes('Z')) {
      time = timePart.replace('Z', '').split('.')[0]
    } else if (timePart.includes('+') || timePart.includes('-')) {
      time = timePart.split(/[+-]/)[0]
    } else {
      time = timePart
    }

    const hour = time.split(':')[0]
    return parseInt(hour, 10) || 0
  }

  return new Date(dateString).getHours()
}

export function formatDateTime(dateString: string, language: AppLanguage = 'es'): string {
  const date = new Date(dateString)
  return date.toLocaleDateString(getLocale(language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Intersección de dos rangos ISO (YYYY-MM-DD). Devuelve null si no se solapan. */
export function intersectDateRanges(
  aDesde: string,
  aHasta: string,
  bDesde: string,
  bHasta: string
): { desde: string; hasta: string } | null {
  const desde = aDesde > bDesde ? aDesde : bDesde
  const hasta = aHasta < bHasta ? aHasta : bHasta
  if (desde > hasta) return null
  return { desde, hasta }
}

export function getCurrentMonthRangeISO(): { desde: string; hasta: string } {
  const d = getBogotaDate()
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const monthStr = String(month).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  return {
    desde: `${year}-${monthStr}-01`,
    hasta: `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
  }
}

export function getTodayISO(): string {
  const d = getBogotaDate()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function isDateInRange(date: string, desde: string, hasta: string): boolean {
  return date >= desde && date <= hasta
}
